import React, { useEffect, useRef, useState } from "react";

const EMBED_SRC = "https://api.liftlocaldigital.com/js/form_embed.js";
const FORM_ID = "rWKMEEBtbcEuDreoLKUI";
const FORM_URL = `https://api.liftlocaldigital.com/widget/form/${FORM_ID}`;

declare global {
  interface Window {
    __llFormEmbedLoaded?: boolean;
    __llFormEmbedQueue?: string[];
    iFrameResize?: (
      options: Record<string, unknown>,
      selector?: string | HTMLElement,
    ) => void;
  }
}

/**
 * Loads the LiftLocal form embed script exactly once and registers every
 * form instance so they all initialise reliably (no duplicate-ID clashes).
 */
function ensureEmbedScript(iframeId: string) {
  if (!window.__llFormEmbedQueue) window.__llFormEmbedQueue = [];

  // Track this instance
  if (!window.__llFormEmbedQueue.includes(iframeId)) {
    window.__llFormEmbedQueue.push(iframeId);
  }

  if (window.__llFormEmbedLoaded) {
    // Script already loaded — re-run resize for this iframe now.
    initIframe(iframeId);
    return;
  }

  window.__llFormEmbedLoaded = true;

  const onLoad = () => {
    window.__llFormEmbedQueue?.forEach(initIframe);
  };

  const script = document.createElement("script");
  script.src = EMBED_SRC;
  script.async = true;
  script.onload = onLoad;
  // Fallback: poll for iFrameResize a few times in case onload fires early.
  let polls = 0;
  const poll = window.setInterval(() => {
    if (typeof window.iFrameResize === "function") {
      window.clearInterval(poll);
      onLoad();
    } else if (++polls > 40) {
      window.clearInterval(poll);
    }
  }, 150);
  document.body.appendChild(script);
}

function initIframe(iframeId: string) {
  if (typeof window.iFrameResize !== "function") return;
  const el = document.getElementById(iframeId);
  if (!el) return;
  try {
    window.iFrameResize(
      {
        log: false,
        checkOrigin: false,
        enablePublicMethods: true,
        scrolling: true,
        heightCalculationMethod: "offset",
        autoResize: true,
        sizeWidth: false,
        sizeHeight: true,
      },
      `#${iframeId}`,
    );
  } catch {
    /* already initialised */
  }
}

interface FormEmbedProps {
  /** unique suffix so each instance gets its own DOM id */
  instanceId: string;
  /** height of the iframe */
  height?: number | string;
  /** when true (default for below-fold forms), the iframe src only loads once
   *  it scrolls near the viewport — saves ~1MB of third-party JS on load */
  lazy?: boolean;
  /** when true (default for above-fold forms), defer mounting the iframe +
   *  the third-party embed script until the browser is idle / the page has
   *  loaded. Keeps ~250 KiB of third-party JS off the critical render path
   *  (the single biggest TBT / LCP-render-delay contributor on this site)
   *  while still showing the form within ~1s. */
  defer?: boolean;
  className?: string;
}

export const FormEmbed: React.FC<FormEmbedProps> = ({
  instanceId,
  height = 460,
  lazy = false,
  defer = false,
  className = "w-full",
}) => {
  const iframeId = `inline-${FORM_ID}-${instanceId}`;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(!lazy && !defer);

  // Lazy-load: only mount the iframe when it nears the viewport.
  useEffect(() => {
    if (!lazy || visible) return;
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [lazy, visible]);

  // Defer: wait until the browser is idle (or the load event fires, or a
  // 1.2s safety net) before mounting the iframe + heavy embed script. This
  // keeps the form's ~250 KiB of third-party JS off the LCP critical path.
  useEffect(() => {
    if (!defer || visible) return;
    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      setVisible(true);
    };
    const ric =
      window.requestIdleCallback ||
      ((cb: () => void) => window.setTimeout(cb, 200));
    const handle = ric(show, { timeout: 1200 }) as number;
    const fallback = window.setTimeout(show, 1200);
    if (document.readyState === "complete") {
      show();
    } else {
      window.addEventListener("load", show, { once: true });
    }
    return () => {
      window.cancelIdleCallback?.(handle);
      window.clearTimeout(fallback);
      window.removeEventListener("load", show);
    };
  }, [defer, visible]);

  // Once the iframe is in the DOM, register it with the embed script.
  useEffect(() => {
    if (!visible) return;
    // small delay so the iframe element is definitely in the DOM
    const t = window.setTimeout(() => ensureEmbedScript(iframeId), 50);
    return () => window.clearTimeout(t);
  }, [visible, iframeId]);

  const heightStyle = typeof height === "number" ? `${height}px` : height;

  return (
    <div ref={wrapRef} className={className} style={{ minHeight: heightStyle }}>
      {visible ? (
        <iframe
          src={FORM_URL}
          style={{
            width: "100%",
            height: heightStyle,
            border: "none",
            borderRadius: "10px",
          }}
          id={iframeId}
          data-layout="{'id':'INLINE'}"
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name="Website Form "
          data-height={typeof height === "number" ? String(height) : undefined}
          data-layout-iframe-id={iframeId}
          data-form-id={FORM_ID}
          title="Website Form "
          className="w-full"
        />
      ) : (
        // Skeleton placeholder keeps layout stable (no CLS) while the form
        // is deferred off the critical render path.
        <div
          aria-busy="true"
          aria-label="Loading quote form"
          style={{
            width: "100%",
            height: heightStyle,
            borderRadius: "10px",
          }}
          className="animate-pulse bg-stone-100 border border-stone-200"
        />
      )}
    </div>
  );
};
